import { Component } from '@angular/core';
import { AuthService, BreadcrumbService, ProductNameService, StateService, ConfirmDialogService, Utils } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ProductName } from 'situview-webapi-client';
import {
  GetRecipeEventSummaryByArtifactIdRequest, GetRecipeEventSummaryByArtifactIdResponse, RecipeEvent, RecipeEventSummary, RecipeEventType, SituAnalyticsWebApiClient, UpdateRecipeEventSummaryStatusRequest
} from 'situanalytics-webapi-client';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from 'src/app/services/config.service';
import * as moment from 'moment';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ArtifactViewerDialogComponent } from 'src/app/components';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarpApiService } from 'src/app/services/warpapi.service';
import { GetArtifactResponse, Artifact, SearchArtifactsRequest, GetArtifactsResponse, SituWarpApiClient, GetArtifactRequest } from 'situ-warpapi-client';
import { isDaydreamProduct } from 'src/app/shared/models/daydream-product';
import { SituViewWebApiClient, GetSituViewRequest, GetSituViewResponse, RenderedOutput, SetArtifactActiveRequest, SetRenderedOutputStatusRequest, SetRenderedOutputStatusResponse, RenderedOutputStatus } from 'situview-webapi-client';

@Component({
  selector: 'recipe-event-summary',
  templateUrl: './recipe-event-summary.component.html',
  styleUrls: ['./recipe-event-summary.component.scss']
})
export class RecipeEventSummaryComponent extends SituBaseComponent {
  private readonly IdParamName: string = "id";

  id: string = "";
  recipeEventSummary: RecipeEventSummary | undefined;
  recipeEventSummaryGrouped?: RecipeEventSummaryGroupedEvents;
  timelineEvents: TimelineEvent[] | undefined;
  showTimeline: boolean = true;
  updating = false;
  artifactInfo: Artifact | undefined;
  renderedOutputInfo: RenderedOutput | undefined;

  productName = ProductName;
  status = RenderedOutputStatus;

  dialogRef: MatDialogRef<ArtifactViewerDialogComponent> | undefined;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private breadcrumbService: BreadcrumbService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    private productNameService: ProductNameService,
    private route: ActivatedRoute,
    private config: ConfigService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private confirmDialog: ConfirmDialogService,
    private warpApiService: WarpApiService,
    private situWarpApiClient: SituWarpApiClient,
    private situViewWebApiClient: SituViewWebApiClient
  ) {
    super(authService, stateService);
    if (this.route.snapshot.params[this.IdParamName]) {
      this.id = this.route.snapshot.params[this.IdParamName];
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.loadRecipeEventSummary();
    this.loadArtifactInfo();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.destroyed = true;
    this.setPolling(false);
  }

  /* polling */
  private readonly PollingIntervalMs = 20000;
  private readonly LateRefreshGraceMs = 10000;
  private readonly MaxPollingFails = 3;
  private readonly MaxQueuedPollingAgeMs = 24 * 60 * 60 * 1000;

  isPolling = false;
  pollingFails = 0;
  now = Date.now();

  // When the next automatic refresh should land; unset while paused (tab hidden) or not polling.
  private pollDueAt: number | undefined;
  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  private refreshing = false;
  private destroyed = false;

  get pollingStoppedAfterFailures(): boolean {
    return this.pollingFails >= this.MaxPollingFails;
  }

  // Covers a slow or hung request as well as a throttled timer.
  get isAutoRefreshLate(): boolean {
    return this.isPolling && this.pollDueAt !== undefined
      && this.now - this.pollDueAt > this.LateRefreshGraceMs;
  }

  get showManualRefresh(): boolean {
    return !this.isPolling || this.isAutoRefreshLate;
  }

  get manualRefreshTooltip(): string {
    if (this.pollingStoppedAfterFailures) return 'Automatic refresh stopped after repeated errors. Refresh now';
    if (this.isAutoRefreshLate) return 'Automatic refresh is running late. Refresh now';
    return 'Refresh';
  }

  // Always sends a request, even over a hung one, since the user has asked for it. Polling is
  // re-armed first so a failed attempt is retried on the normal schedule rather than left stopped.
  manualRefresh() {
    this.pollingFails = 0;
    if (this.recipeEventSummary) {
      this.setPolling(this.shouldPollRenderStatus(this.recipeEventSummary));
    }
    this.refreshRenderStatus(true);
  }

  // Refresh straight away on return to the tab; no polling while it is hidden.
  private readonly onVisibilityChange = () => {
    if (!this.isPolling) return;
    if (document.hidden) {
      this.clearPollingTimer();
      this.pollDueAt = undefined;
    } else {
      this.refreshRenderStatus();
    }
  };

  private setPolling(active: boolean) {
    // A response can land after the page is left; it must not restart the timers.
    active = active && !this.destroyed;
    this.isPolling = active;
    if (!active) {
      this.clearPollingTimer();
      this.pollDueAt = undefined;
    }

    if (active && !this.clockTimer) {
      this.clockTimer = setInterval(() => { this.now = Date.now(); }, 1000);
    } else if (!active && this.clockTimer) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  }

  // Chained timeouts, so a slow response never overlaps the next poll.
  private schedulePoll() {
    this.clearPollingTimer();
    if (this.isPolling && !document.hidden) {
      this.pollDueAt = Date.now() + this.PollingIntervalMs;
      this.pollingTimer = setTimeout(() => this.refreshRenderStatus(), this.PollingIntervalMs);
    }
  }

  private clearPollingTimer() {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  private refreshRenderStatus(force: boolean = false) {
    if (this.refreshing && !force) return;
    this.clearPollingTimer();
    this.pollDueAt ??= Date.now();
    this.loadRecipeEventSummary(true);
    this.loadArtifactInfo();
  }

  private recordPollingFailure() {
    this.pollingFails++;
    if (this.pollingStoppedAfterFailures) {
      this.setPolling(false);
    }
  }

  // Polls while in progress, or while waiting to start. A render waiting over a day is treated as stuck.
  private shouldPollRenderStatus(recipeEventSummary: RecipeEventSummary): boolean {
    if (recipeEventSummary.dateCompleted || recipeEventSummary.dateFailed || recipeEventSummary.dateCancelled) {
      return false;
    }
    if (recipeEventSummary.dateStarted) {
      return true;
    }

    const waitingSince = recipeEventSummary.dateQueued ?? recipeEventSummary.dateAdded;
    return !!waitingSince && Date.now() - waitingSince.getTime() < this.MaxQueuedPollingAgeMs;
  }
  /* polling */

  toggleTimeline() {
    this.showTimeline = !this.showTimeline;
  }

  highlightedEventId: string | undefined;
  private highlightTimer: ReturnType<typeof setTimeout> | null = null;

  // Opens the complete timeline if needed, then scrolls to the event's row and briefly highlights it.
  scrollToEvent(eventId: string) {
    this.showTimeline = true;
    this.highlightedEventId = eventId;

    setTimeout(() => {
      document.getElementById(`recipe-event-${eventId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    if (this.highlightTimer) clearTimeout(this.highlightTimer);
    this.highlightTimer = setTimeout(() => { this.highlightedEventId = undefined; }, 2500);
  }

  viewArtifactFile(item: RecipeEvent) {
    if (!item?.fileName) return;

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    var fileNames = this.recipeEventSummary!.recipeEvents!.filter(x => this.isLink(x)).map(x => x.fileName!);
    var distinctFiles = new Set<string>(fileNames);

    this.dialogRef = this.dialog.open(ArtifactViewerDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: {
        fileName: item.fileName,
        artifactId: item.recipeArtifactId,
        additionalFileNames: [...distinctFiles],
      }
    });

  }

  // A quiet load is a background poll: no loading spinner, and failures count towards stopping.
  loadRecipeEventSummary(quiet: boolean = false) {
    if (quiet) {
      this.refreshing = true;
    } else {
      this.loading = true;
    }
    let request = new GetRecipeEventSummaryByArtifactIdRequest();
    request.artifactId = this.id;
    request.includeSummaries = true;

    return this.situAnalyticsClient.getRecipeEventSummaryByArtifactId(request)
      .then((response: GetRecipeEventSummaryByArtifactIdResponse) => {
        if (response.success && response.recipeEventSummary) {
          this.recipeEventSummary = response.recipeEventSummary;
          let sortedEvents = response.recipeEventSummary.recipeEvents!.sort((a, b) => a.dateTimeStamp!.getTime() - b.dateTimeStamp!.getTime());
          this.recipeEventSummary.recipeEvents = sortedEvents;
          this.timelineEvents = this.getRecipeEventErrors();
          if (this.recipeEventSummary) {

            this.recipeEventSummaryGrouped = new RecipeEventSummaryGroupedEvents(sortedEvents);

            //this.updateTimelineChartData();
            this.pollingFails = 0;
            this.setPolling(this.shouldPollRenderStatus(this.recipeEventSummary));
          }
          // Load rendered output info after recipe event summary is loaded
          this.loadRenderedOutputInfo();
        } else if (quiet) {
          this.recordPollingFailure();
        }
      })
      .catch((x: any) => {
        //this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
        if (quiet) {
          this.recordPollingFailure();
        }
      })
      .finally(() => {
        if (quiet) {
          this.refreshing = false;
        } else {
          this.loading = false;
        }
        this.schedulePoll();
      });
  }

  loadArtifactInfo() {
    if (!this.id) return;

    // Search for the artifact using the broad search approach
    const request = new GetArtifactRequest();
    request.artifactId = this.id;
    request.returnFiles = false;
    request.returnMetaData = true;
    request.returnSourceArtifacts = false;

    this.situWarpApiClient.getArtifact(request)
      .then((response: GetArtifactResponse) => {
        if (response.success && response.artifact) {
          this.artifactInfo = response.artifact;
        }
      })
      .catch((error: any) => {
        console.error('Error loading artifact info:', error);
      });
  }

  loadRenderedOutputInfo() {
    if (!this.recipeEventSummary?.renderedOutputId) return;

    const request = new GetSituViewRequest();
    request.renderedOutputId = this.recipeEventSummary.renderedOutputId;
    request.includeHistory = false;

    this.situViewWebApiClient.getSituView(request)
      .then((response: GetSituViewResponse) => {
        if (response.success && response.id) {
          // GetSituViewResponse itself contains the rendered output properties
          this.renderedOutputInfo = response as any as RenderedOutput;
        }
      })
      .catch((error: any) => {
        console.error('Error loading rendered output info:', error);
      });
  }

  getEventType(item: RecipeEvent): string {
    if (!item?.recipeEventType) return "";
    return RecipeEventType[item.recipeEventType];
  }

  getEventMessage(item: RecipeEvent) {
    // An error's own text matters more than the stage it happened in
    if (item?.recipeEventType === RecipeEventType.Error && item.event) {
      return item.software ? `${item.software}: ${item.event}` : item.event;
    }
    return item?.fileName ? item?.fileName : item?.software ? item?.software : item?.event;
  }

  isComplete(recipeEventSummary: RecipeEventSummary) {
    return recipeEventSummary?.dateCompleted;
  }

  // Only once complete, as the link has nothing to show before then.
  get situLinkUrl(): string | null {
    if (!this.id || !this.recipeEventSummary || !this.isComplete(this.recipeEventSummary)) return null;

    const productName = this.getProductName(this.recipeEventSummary.productName as any);
    if (!productName) return null;

    return this.config.config.situLinkBaseUrl ?
      `https://${productName}.${this.config.config.situLinkBaseUrl}/${this.id}` :
      `${this.config.config.situPortalBaseUrl}/${this.id}`;
  }

  getStatusIconAndClass(recipeEventSummary: RecipeEventSummary) {
    // in progress
    const isInProgress = !recipeEventSummary.dateCancelled && !recipeEventSummary.dateFailed
      && !recipeEventSummary.dateCompleted && !!recipeEventSummary.dateStarted;
    if (isInProgress) {
      return { icon: "pending", class: "text-info", description: "In Progress" };
    }

    // completed
    if (!!recipeEventSummary.dateCompleted) {
      return { icon: "check_circle", class: "text-success", description: "Completed" };
    }

    // failed
    if (!!recipeEventSummary.dateFailed) {
      return { icon: "error", class: "text-danger", description: "Failed" };
    }

    // cancelled
    if (!!recipeEventSummary.dateCancelled) {
      return { icon: "block", class: "text-muted", description: "Cancelled" };
    }

    // queued
    if (!!recipeEventSummary.dateQueued) {
      return { icon: "pending", class: "text-muted", description: "Queued" };
    }
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  getRecipeEventErrors() {
    if (!this.recipeEventSummary || !this.recipeEventSummary.recipeEvents) return [];
    let events = new Array<TimelineEvent>();

    let firstJobStart = this.recipeEventSummary.recipeEvents.sort((a, b) => a.dateTimeStamp!.getTime() - b.dateTimeStamp!.getTime()).find(x => x.recipeEventType === 1)
    this.recipeEventSummary.recipeEvents.forEach((x: RecipeEvent) => {
      if (x.recipeEventType === 2) {
        events.push(new TimelineEvent(x.dateTimeStamp!, x.event!, "warning"));
      } else if (x.recipeEventType === 8 && x.software) {
        events.push(new TimelineEvent(x.dateTimeStamp!, `started ${x.software!.replace('software\\', '')}`, "info"));
      } else if (x.recipeEventType === 6) {
        events.push(new TimelineEvent(x.dateTimeStamp!, "Queued", "success"));
      }
    });
    if (this.recipeEventSummary.dateCompleted) {
      events.push(new TimelineEvent(this.recipeEventSummary.dateCompleted, "Completed", "success"));
    }
    // Queued-only summaries, and failures before a job started, have no JobStart to place it at
    if (this.recipeEventSummary.dateAdded && firstJobStart?.dateTimeStamp) {
      events.push(new TimelineEvent(firstJobStart.dateTimeStamp, "Started", "success"));
    }

    let sortedEvents = events.sort((a, b) => a.eventDateTime.getTime() - b.eventDateTime.getTime());
    return sortedEvents;
  }

  getTimeToNextEvent(events: TimelineEvent[], eventIndex: number): string {
    let currentEvent = events[eventIndex];
    let returnString = "";

    let nextEventIndex = eventIndex + 1;
    if (nextEventIndex >= events.length) {
      return returnString;
    }
    let nextEvent = events[nextEventIndex];

    if (currentEvent?.eventDateTime && nextEvent?.eventDateTime) {
      let diff = moment(nextEvent.eventDateTime).diff(moment(currentEvent.eventDateTime));
      let duration = moment.duration(diff);

      var days = duration.days();
      if (days) {
        returnString += `${days}d `;
      }

      var hours = duration.hours();
      if (hours) {
        returnString += `${hours}h `;
      }

      var minutes = duration.minutes();
      if (minutes) {
        returnString += `${minutes}m `;
      }

      var seconds = duration.seconds();
      if (seconds) {
        returnString += `${seconds}s `;
      }

      var milliseconds = duration.milliseconds();
      if (!days && !hours && !minutes) {
        returnString += `${milliseconds}ms `;
      }
    }

    return returnString;
  }

  getTimeToNextRecipeEvent(events: RecipeEvent[], eventIndex: number): string {
    let currentEvent = events[eventIndex];

    let nextEventIndex = eventIndex + 1;
    if (nextEventIndex >= events.length) {
      return '';
    }
    let nextEvent = events[nextEventIndex];

    let returnString = getTimeBetweenEvents(currentEvent, nextEvent);

    return returnString;
  }

  getSeqUrl(value: string, isCorrelationId: boolean): string {
    const envName = this.config.config.envName;
    let seqUrl: URL;

    if (envName === 'dev') {
      seqUrl = new URL('http://seq.situflow.com:5341');
    } else {
      seqUrl = new URL('https://seq.situsystems.com');
    }
    seqUrl.pathname = 'events';
    seqUrl.searchParams.set('filter', isCorrelationId ? `CorrelationId = '${value}'` : `ArtifactId = '${value}'`);
    if (envName === 'Staging') seqUrl.searchParams.set('signal', 'signal-26');
    if (envName === 'Production') seqUrl.searchParams.set('signal', 'signal-28');

    return seqUrl.toString().replace('/events', '#/events');
  }

  getRenderedOutputRoute(productName: ProductName | undefined): string | null {
    switch (productName) {
      case ProductName.Facade:
        return "/situ-facade";
      case ProductName.DayDreamFacade:
        return "/situ-daydream";
      case ProductName.Panorama:
      case ProductName.RayTracedDraft:
      case ProductName.RayTraced:
      case ProductName.RayTracedPlus:
      case ProductName.Insights:
      case ProductName.Plan:
      case ProductName.VR:
      case ProductName.Render:
        return "/situ-view";
      default:
        return null;
    }
  }

  /** The Daydream page takes the rendered output id in the path; the others take ?id=. */
  renderedOutputIdInPath(productName: ProductName | undefined): boolean {
    return isDaydreamProduct(productName);
  }

  timeAgoString(date?: Date): string {
    return Utils.timeAgoString(date);
  }

  isOlderThan2Minutes(date: Date) {
    const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000); // 2 minutes in milliseconds
    return date < twoMinutesAgo;
  }

  isLink(item: RecipeEvent): boolean {
    return (item && item.dateTimeStamp && item.fileName &&
      item.recipeEventType === 5 &&
      (item.fileName.endsWith('.jpg') || item.fileName.endsWith('.webp') || item.fileName.endsWith('.glb'))) || false;
  }

  getProductName(productName: ProductName) {
    if (productName === undefined) return null;
    switch (productName) {
      case ProductName.Panorama:
      case ProductName.RayTracedDraft:
      case ProductName.RayTraced:
      case ProductName.RayTracedPlus:
      case ProductName.Insights:
        return "view";
      case ProductName.Plan:
        return "plan";
      case ProductName.Render:
        return "render";
      case ProductName.Facade:
      case ProductName.DayDreamFacade:
        return "facade";
      case ProductName.ProductPreview:
        return "product";
      case ProductName.Connect:
        return "connect";
      default:
        return null;
    }
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  getArtifactType(artifact: Artifact | undefined): string | undefined {
    if (!artifact?.metaData) return undefined;
    const typeMetadata = artifact.metaData.find(md => md.key === 'Type');
    return typeMetadata?.value;
  }

  getArtifactInstanceName(artifact: Artifact | undefined): string | undefined {
    if (!artifact?.metaData) return undefined;
    const instanceMetadata = artifact.metaData.find(md => md.key === 'InstanceName');
    return instanceMetadata?.value;
  }

  getStyleTemplateName(renderedOutput: RenderedOutput | undefined): string | undefined {
    if (!renderedOutput) return undefined;
    // Extract template name from styleConfigTemplate.name
    return (renderedOutput as any)?.styleConfigTemplate?.name;
  }

  getStyleTemplateId(renderedOutput: RenderedOutput | undefined): string | undefined {
    if (!renderedOutput) return undefined;
    // Extract template ID from styleConfigTemplate.id
    return (renderedOutput as any)?.styleConfigTemplate?.id;
  }

  getRenderedOutputStatusColor(status: RenderedOutputStatus): string {
    switch (status) {
      case RenderedOutputStatus.Complete:
        return '#4caf50'; // green
      case RenderedOutputStatus.InProgress:
      case RenderedOutputStatus.Queued:
        return '#2196f3'; // blue
      case RenderedOutputStatus.Failed:
        return '#f44336'; // red
      case RenderedOutputStatus.Cancelled:
        return '#9e9e9e'; // gray
      default:
        return '#9e9e9e'; // gray
    }
  }

  getRenderedOutputStatusIcon(status: RenderedOutputStatus): string {
    switch (status) {
      case RenderedOutputStatus.Complete:
        return 'check_circle';
      case RenderedOutputStatus.InProgress:
        return 'pending';
      case RenderedOutputStatus.Queued:
        return 'schedule';
      case RenderedOutputStatus.Failed:
        return 'error';
      case RenderedOutputStatus.Cancelled:
        return 'cancel';
      default:
        return 'help';
    }
  }

  canUpdateStatus(): boolean {
    if (!this.recipeEventSummary) return false;
    
    // Can only update if the recipe is in progress or queued
    const isInProgress = !this.recipeEventSummary.dateCancelled && !this.recipeEventSummary.dateFailed
      && !this.recipeEventSummary.dateCompleted && !!this.recipeEventSummary.dateStarted;
    const isQueued = !this.recipeEventSummary.dateCancelled && !this.recipeEventSummary.dateFailed
      && !this.recipeEventSummary.dateCompleted && !this.recipeEventSummary.dateStarted && !!this.recipeEventSummary.dateQueued;
    
    return isInProgress || isQueued;
  }

  updateStatus(markAsFailed: boolean, markAsCancelled: boolean) {
    if (!this.recipeEventSummary?.id || this.updating) return;

    const action = markAsFailed ? 'fail' : 'cancel';
    const actionColour = markAsFailed ? 'warn' : 'basic';
    
    this.confirmDialog.open({
      title: `Confirm ${action}`,
      message: `Are you sure you want to ${action} this recipe event summary?`,
      confirmText: 'Yes',
      cancelText: 'No',
      actionIcon: markAsFailed ? 'error' : 'cancel',
      actionColour: actionColour
    });

    this.confirmDialog.confirmed().subscribe(result => {
      if (result) {
        this.performStatusUpdate(markAsFailed, markAsCancelled);
      }
    });
  }

  private performStatusUpdate(markAsFailed: boolean, markAsCancelled: boolean) {
    if (!this.recipeEventSummary?.id) return;

    this.updating = true;
    const request = new UpdateRecipeEventSummaryStatusRequest();
    request.recipeEventSummaryId = this.recipeEventSummary.id;
    request.markAsFailed = markAsFailed;
    request.markAsCancelled = markAsCancelled;
    request.reason = markAsFailed ? 'Marked as failed by user' : 'Cancelled by user';

    this.situAnalyticsClient.updateRecipeEventSummaryStatus(request)
      .then(response => {
        if (response.success) {
          const action = markAsFailed ? 'failed' : 'cancelled';
          this.showMessage(`Recipe event summary marked as ${action}`);
          // Reload the data to show updated status
          this.loadRecipeEventSummary();
        } else {
          this.showMessage('Failed to update status');
        }
      })
      .catch(error => {
        console.error('Error updating status:', error);
        this.showMessage('Error updating status');
      })
      .finally(() => {
        this.updating = false;
      });
  }

  toggleArtifactActive() {
    if (!this.artifactInfo?.callerReferenceKey || !this.artifactInfo?.version) return;

    this.confirmDialog.open({
      title: `${this.artifactInfo.active ? 'Disable' : 'Enable'} Artifact`,
      message: `Are you sure you want to ${this.artifactInfo.active ? 'disable' : 'enable'} this artifact?`,
      confirmText: 'Yes',
      cancelText: 'No',
      actionIcon: this.artifactInfo.active ? 'toggle_off' : 'toggle_on',
      actionColour: this.artifactInfo.active ? 'warn' : 'primary'
    });

    this.confirmDialog.confirmed().subscribe(result => {
      if (result) {
        this.performArtifactToggle();
      }
    });
  }

  private performArtifactToggle() {
    if (!this.artifactInfo?.callerReferenceKey || !this.artifactInfo?.version) return;

    const request = new SetArtifactActiveRequest();
    request.callerReferenceKey = this.artifactInfo.callerReferenceKey;
    request.version = this.artifactInfo.version;
    request.active = !this.artifactInfo.active;

    this.situViewWebApiClient.setArtifactActive(request)
      .then(response => {
        if (response.success) {
          this.artifactInfo!.active = request.active;
          const action = request.active ? 'enabled' : 'disabled';
          this.showMessage(`Artifact ${action} successfully`);
        } else {
          this.showMessage('Failed to update artifact status');
        }
      })
      .catch(error => {
        console.error('Error updating artifact status:', error);
        this.showMessage('Error updating artifact status');
      });
  }

  setRenderedOutputStatus(status: RenderedOutputStatus) {
    if (!this.renderedOutputInfo?.artifactId) return;

    const statusName = status === RenderedOutputStatus.Cancelled ? 'cancel' : 'mark as error';
    
    this.confirmDialog.open({
      title: `${statusName.charAt(0).toUpperCase() + statusName.slice(1)} Rendered Output`,
      message: `Are you sure you want to ${statusName} this rendered output?`,
      confirmText: 'Yes',
      cancelText: 'No',
      actionIcon: status === RenderedOutputStatus.Cancelled ? 'cancel' : 'error',
      actionColour: status === RenderedOutputStatus.Cancelled ? 'basic' : 'warn'
    });

    this.confirmDialog.confirmed().subscribe(result => {
      if (result) {
        this.performRenderedOutputStatusUpdate(status);
      }
    });
  }

  private performRenderedOutputStatusUpdate(status: RenderedOutputStatus) {
    if (!this.renderedOutputInfo?.artifactId) return;

    const request = new SetRenderedOutputStatusRequest();
    request.renderedOutputId = this.renderedOutputInfo.artifactId;
    request.status = status;

    this.situViewWebApiClient.setRenderedOutputStatus(request)
      .then((response: SetRenderedOutputStatusResponse) => {
        if (response.success) {
          this.renderedOutputInfo!.status = status;
          const statusName = status === RenderedOutputStatus.Cancelled ? 'cancelled' : 'marked as error';
          this.showMessage(`Rendered output ${statusName} successfully`);
        } else {
          this.showMessage('Failed to update rendered output status');
        }
      })
      .catch(error => {
        console.error('Error updating rendered output status:', error);
        this.showMessage('Error updating rendered output status');
      });
  }
}

class TimelineEvent {
  constructor(
    public eventDateTime: Date,
    public eventMessage: string,
    public eventTypeStyle: string) {
  }
}

function getTimeBetweenEvents(prevEvent: RecipeEvent | undefined | null, nextEvent: RecipeEvent | undefined | null) {
  let returnString = "";

  if (prevEvent?.dateTimeStamp && nextEvent?.dateTimeStamp) {
    let diff = moment(nextEvent.dateTimeStamp).diff(moment(prevEvent.dateTimeStamp), 'ms');
    let duration = moment.duration(diff, 'ms');

    const totalSeconds = diff / 1000;
    const totalMinutes = totalSeconds / 60;
    const totalHours = totalMinutes / 60;
    const totalDays = totalHours / 24;

    var days = duration.days();
    if (days) {
      returnString += `${days}d `;
    }

    var hours = duration.hours();
    if (hours) {
      returnString += `${hours}h `;
    }

    var minutes = duration.minutes();
    if (minutes) {
      returnString += `${minutes}m `;
    }

    var seconds = duration.seconds();
    if (seconds) {
      returnString += `${seconds}s `;
    }

    if (totalSeconds < 1) {
      var milliseconds = duration.milliseconds();
      if (!days && !hours && !minutes) {
        returnString += `${milliseconds}ms `;
      }
    }
  }
  return returnString;
}


interface RecipeEventSummaryGroupOfEvents {

  dateTimeStamp: Date;

  timeSinceLastGroup: string;

  events: RecipeEvent[];
}


class RecipeEventSummaryGroupedEvents {

  public groups: RecipeEventSummaryGroupOfEvents[];
  constructor(private events: RecipeEvent[]) {
    const groupedEvents = groupCloseEvents(events, 5, 20);

    this.groups = [];

    for (const srcGroup of groupedEvents) {
      if (srcGroup.length === 0) continue;

      const prevGroup = this.groups.length > 0 ? this.groups[this.groups.length - 1] : null;

      const newGroup: RecipeEventSummaryGroupOfEvents = {
        events: srcGroup,
        dateTimeStamp: srcGroup[0].dateTimeStamp ?? new Date(),
        timeSinceLastGroup: getTimeBetweenEvents(prevGroup?.events[0], srcGroup[0]),
      }

      this.groups.push(newGroup)
    }

    if (this.groups.length > 0) {
      const lastGroup = this.groups[this.groups.length - 1];

      if (lastGroup.events.length > 0) {
        const firstEvent = lastGroup.events[0];
        const lastEvent = lastGroup.events[lastGroup.events.length - 1];

        const timeDiff = getTimeBetweenEvents(firstEvent, lastEvent);

        this.groups.push({
          dateTimeStamp: lastEvent.dateTimeStamp ?? new Date(),
          events: [],
          timeSinceLastGroup: timeDiff,
        })
      }
    }


  }



}


function groupCloseEvents(events: RecipeEvent[], maxGapSeconds: number, maxGroupSeconds: number): RecipeEvent[][] {
  const groups: RecipeEvent[][] = [];
  let currentGroup: RecipeEvent[] = [];

  for (let i = 0; i < events.length; i++) {
    const current = events[i];

    if (!current.dateTimeStamp) {
      continue; // Skip events without timestamps
    }

    if (currentGroup.length === 0) {
      currentGroup.push(current);
    } else {
      const first = currentGroup[0];
      const previous = currentGroup[currentGroup.length - 1];
      if (!previous.dateTimeStamp || !first.dateTimeStamp) {
        continue;
      }

      const diffToLastEventMilliseconds = current.dateTimeStamp.getTime() - previous.dateTimeStamp.getTime();
      const diffToLastEventSeconds = diffToLastEventMilliseconds / 1000;

      const diffToFirstGroupedEventMilliseconds = current.dateTimeStamp.getTime() - first.dateTimeStamp.getTime();
      const diffToFirstGroupedEventSeconds = diffToFirstGroupedEventMilliseconds / 1000;


      if (diffToLastEventSeconds <= maxGapSeconds && diffToFirstGroupedEventSeconds < maxGroupSeconds) {
        currentGroup.push(current);
      } else {
        groups.push(currentGroup);
        currentGroup = [current];
      }
    }
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}
